/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      // If no variant is found for the selected options, or variant is offline, find the first available variant that matches by reconfiguring options beneath the changed axis
      const isOffline = this.variant && this.data.formatted[this.variant.id]?.variantOffline;
      if (!this.variant || !this.variant.available || isOffline) {
        // Get the index of the changed option selector
        let indexOfChangedSelector = 0;
        const changedSelector = evt.target.closest('.option-selector');
        this.childNodes.forEach((node, index) => {
          if (node === changedSelector) {
            indexOfChangedSelector = index;
          }
        });

        // Find the first available variant by reconfiguring the options beneath the changed axis
        let foundVariant = this.findFirstAvailableVariantFromSelectedAxis(indexOfChangedSelector);
        // If a variant is found that is valid and available, update the variant to the found variant
        if (foundVariant) {
          this.variant = foundVariant;
        }
      }

      if (this.variant) {
        this.updateUrl(evt);
      }

      this.updateAvailability();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            section: this.section,
            form: this.productForm,
            variant: this.variant,
            variantFormatted: this.variant ? this.data.formatted[this.variant.id] : null,
            canBeCustomized: this.variant ? this.data.canBeCustomized[this.variant.id] : false,
            product: this.data.product,
          },
        }),
      );
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.getSelectedOptions() };
      }

      const updateOptionAvailability = (optionEl, available, soldout) => {
        const el = optionEl;
        const text = soldout ? theme.strings.noStock : theme.strings.noVariant;
        el.classList.toggle('is-unavailable', !available);

        if (optionEl.classList.contains('custom-select__option')) {
          const em = el.querySelector('em');

          if (em) {
            em.hidden = available;
          }

          if (!available) {
            if (em) {
              em.textContent = text;
            } else {
              el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${text}</em>`;
            }
          }
        } else if (!available) {
          el.nextElementSibling.title = text;
        } else {
          el.nextElementSibling.removeAttribute('title');
        }
      };

      // Flag all options as unavailable
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        updateOptionAvailability(optionEl, false, false);
      });

      // Flag selector options as available or sold out, depending on the variant availability
      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          let matchCount = 0;

          variant.options.forEach((option, optionIndex) => {
            if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
              matchCount += 1;
            }
          });

          if (matchCount === currVariant.options.length - 1) {
            const options = selector.querySelectorAll('.js-option');
            const optionEl = Array.from(options).find((opt) => {
              if (selector.dataset.selectorType === 'dropdown') {
                return opt.dataset.value === variant.options[selectorIndex];
              }
              return opt.value === variant.options[selectorIndex];
            });

            if (optionEl) {
              updateOptionAvailability(optionEl, variant.available, !variant.available);
            }
          }
        });
      });
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorTexts = selector.querySelectorAll('.js-color-text');
      if (!colorTexts.length) return;

      const selectedValue = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      const activeGroup = evt.target.closest('.swatch-group');

      // Update all color text labels: show the one in the active group, hide the rest
      colorTexts.forEach((colorText) => {
        const isActiveGroup = activeGroup && activeGroup.contains(colorText);
        if (isActiveGroup) {
          colorText.textContent = ': ' + selectedValue;
          colorText.hidden = false;
        } else {
          colorText.hidden = true;
        }
      });
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Finds first available variant of the selected axis, by configuring only the dependent options beneath said axis.
     * @returns {object} - Variant object, or null if one is not found.
     */
    findFirstAvailableVariantFromSelectedAxis(selectorIndex) {
      // Use the options as selected by the user as the starting point for the search
      let selectedOptions = this.getSelectedOptions();

      // Recursively call the findNextAvailableVariantRecursively function, starting with the next level deeper in the selector tree, as we don't want to override the last axis changed by the user.
      // ex. if a user switched the color input, we don't want to switch to another color, but rather find the first available sheath, size, etc. for the selected color
      return this.findNextAvailableVariantRecursively(selectedOptions, selectorIndex + 1);
    }

    /**
     * Finds the next available variant recursively, only searching at and beneath the selector index provided.
     * @param {Array} selectedOptions - The selected options to start the search with.
     * @param {int} selectorIndex - The index of the selector to start the search at.
     * @returns {object} - Variant object, or null if one is not found.
     */
    findNextAvailableVariantRecursively(selectedOptions, selectorIndex) {
      // If we have not yet reached the end of the option selector tree, recursively call this function
      if (selectorIndex < this.optionSelectors.length) {
        // Get all options for the current selector
        let options = this.optionSelectors[selectorIndex].querySelectorAll('.js-option');

        // Loop through all options for the current selector
        for (let i = 0; i < options.length; i++) {
          // Get the value of the current option
          const optionValue = options[i].value;
          // Create a new array from the entire option set with the current option value at the current selector index
          const newSelectedOptions = selectedOptions.slice();
          newSelectedOptions[selectorIndex] = optionValue;

          // Recursively call this function with the new selected options and the next selector index deeper in the tree
          let foundVariant = this.findNextAvailableVariantRecursively(newSelectedOptions, selectorIndex + 1);

          // If a non-null result is found, then a valid and available variant was found, immediately return the first one found
          if (foundVariant) {
            return foundVariant;
          }
        }
        // If no variants were found, return null
        return null;
      } else {
        // We have reached the end of the option selector tree, check if a variant exists with the selected options
        const variant = this.data.product.variants.find((v) => v.options.every((val, i) => val === selectedOptions[i]));

        // If a variant is available and not offline, return the variant
        if (variant && variant.available && !this.data.formatted[variant.id]?.variantOffline) {
          return variant;
        } else {
          // If no variant was found, or the variant is not available or offline, return null
          return null;
        }
      }
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      const returnOptions = this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index]),
      );
      return returnOptions;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
